import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { billingService, caseService } from '../services/api';
import '../styles/Dashboard.css';

const KPI_TABS = [
  { key: 'productivity', label: 'Productivity & Time' },
  { key: 'financial', label: 'Financial & Billing' },
  { key: 'business', label: 'Business Development' },
  { key: 'experience', label: 'Client Experience' },
];

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('productivity');
  const [cases, setCases] = useState([]);
  const [billingSummaries, setBillingSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadDashboard = async () => {
      if (!user?.officeId) {
        setError('User session information is missing. Please log in again.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const casesResponse = await caseService.getCasesByOffice(user.officeId);
        const officeCases = Array.isArray(casesResponse.data) ? casesResponse.data : [];
        setCases(officeCases);

        const summaryResults = await Promise.allSettled(
          officeCases.map((caseItem) => billingService.getBillingSummary(caseItem.id))
        );
        setBillingSummaries(
          summaryResults
            .filter((result) => result.status === 'fulfilled')
            .map((result) => result.value.data)
        );
      } catch (err) {
        const responseMessage = typeof err.response?.data === 'string'
          ? err.response.data
          : err.response?.data?.message;
        setError(responseMessage || 'Failed to load dashboard KPIs');
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [user?.officeId]);

  const metrics = useMemo(() => buildMetrics(cases, billingSummaries), [cases, billingSummaries]);
  const cards = metricCards(metrics)[activeTab] || [];

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div>
          <h2>Firm KPI Dashboard</h2>
          <p>{cases.length} active office case{cases.length === 1 ? '' : 's'} included in this view</p>
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="dashboard-tabs">
        {KPI_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={activeTab === tab.key ? 'active' : ''}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="dashboard-state">Loading dashboard KPIs...</p>
      ) : (
        <section className="kpi-grid">
          {cards.map((card) => (
            <article key={card.title} className={`kpi-card ${card.status || ''}`}>
              <div className="kpi-card-head">
                <span>{card.group}</span>
                {card.statusLabel && <strong>{card.statusLabel}</strong>}
              </div>
              <h3>{card.title}</h3>
              <div className="kpi-value">{card.value}</div>
              <p>{card.description}</p>
              {card.note && <small>{card.note}</small>}
            </article>
          ))}
        </section>
      )}
    </div>
  );
}

function buildMetrics(cases, billingSummaries) {
  const timeEntries = billingSummaries.flatMap((summary) => summary?.timeEntries || []);
  const invoices = billingSummaries.flatMap((summary) => summary?.invoices || []);
  const invoiceLines = invoices.flatMap((invoice) => invoice?.lineItems || []);

  const totalHours = sum(timeEntries, (entry) => entry.hours);
  const billableHours = sum(timeEntries.filter((entry) => entry.billable), (entry) => entry.hours);
  const invoicedHours = sum(invoiceLines.filter((line) => line.timeEntryId), (line) => line.quantity);
  const totalInvoiced = sum(invoices.filter((invoice) => invoice.status !== 'VOID'), (invoice) => invoice.totalAmount);
  const totalPaid = sum(invoices, (invoice) => invoice.paidAmount);
  const outstanding = sum(invoices.filter((invoice) => invoice.status !== 'VOID'), (invoice) => invoice.balanceAmount ?? invoice.totalAmount);
  const unbilledValue = billingSummaries.reduce((total, summary) => {
    const unbilledEntries = (summary?.timeEntries || []).filter((entry) => entry.billable && !entry.invoiceId);
    return total + unbilledEntries.reduce((entryTotal, entry) => {
      const rate = Number(entry.rate || summary?.profile?.hourlyRate || 0);
      return entryTotal + Number(entry.hours || 0) * rate;
    }, 0);
  }, 0);
  const closedCases = cases.filter((caseItem) => String(caseItem.status || '').toUpperCase() === 'CLOSED');
  const averageCaseDays = average(
    closedCases
      .map((caseItem) => daysBetween(caseItem.createdAt, caseItem.updatedAt))
      .filter((days) => days !== null)
  );

  return {
    totalHours,
    billableHours,
    invoicedHours,
    totalInvoiced,
    utilizationRate: percent(billableHours, totalHours),
    realizationRate: percent(invoicedHours, billableHours),
    collectionRate: percent(totalPaid, totalInvoiced),
    lockup: unbilledValue + outstanding,
    effectiveHourlyRate: totalHours > 0 ? totalInvoiced / totalHours : null,
    totalPaid,
    outstanding,
    newClients: unique(cases.map((caseItem) => caseItem.clientEmail || caseItem.clientName)).length,
    averageCaseDays,
  };
}

function metricCards(metrics) {
  return {
    productivity: [
      {
        group: 'Time Management',
        title: 'Utilization Rate',
        value: formatPercent(metrics.utilizationRate),
        status: rangeStatus(metrics.utilizationRate, 75, 85),
        statusLabel: 'Target 75%-85%',
        description: 'Percentage of tracked work time spent on billable work.',
        note: `${formatNumber(metrics.billableHours)} billable hours / ${formatNumber(metrics.totalHours)} total hours tracked`,
      },
      {
        group: 'Time Management',
        title: 'Realization Rate',
        value: formatPercent(metrics.realizationRate),
        status: metrics.realizationRate === null ? 'missing' : 'tracked',
        description: 'Percentage of billable hours that made it onto invoices without write-downs.',
        note: `${formatNumber(metrics.invoicedHours)} invoiced hours / ${formatNumber(metrics.billableHours)} billable hours`,
      },
    ],
    financial: [
      {
        group: 'Billing Efficiency',
        title: 'Collection Rate',
        value: formatPercent(metrics.collectionRate),
        status: metrics.collectionRate === null ? 'missing' : 'tracked',
        description: 'Percentage of invoiced value that has been collected.',
        note: `${money(metrics.totalPaid)} paid / ${money(metrics.totalInvoiced)} invoiced`,
      },
      {
        group: 'Billing Efficiency',
        title: 'Lockup',
        value: money(metrics.lockup),
        status: 'tracked',
        description: 'Estimated WIP plus accounts receivable still waiting to convert into cash.',
        note: `Includes ${money(metrics.outstanding)} outstanding invoice balance plus unbilled billable time value.`,
      },
      {
        group: 'Billing Efficiency',
        title: 'Effective Hourly Rate',
        value: metrics.effectiveHourlyRate === null ? 'No hours' : money(metrics.effectiveHourlyRate),
        status: metrics.effectiveHourlyRate === null ? 'missing' : 'tracked',
        description: 'Total invoiced revenue divided by total tracked hours.',
        note: `${money(metrics.totalInvoiced)} invoiced / ${formatNumber(metrics.totalHours)} hours`,
      },
    ],
    business: [
      {
        group: 'Marketing',
        title: 'Client Acquisition Cost',
        value: 'Needs spend data',
        status: 'missing',
        description: 'Marketing and business development spend divided by new clients acquired.',
        note: `${metrics.newClients} unique client${metrics.newClients === 1 ? '' : 's'} currently detected from cases.`,
      },
      {
        group: 'Marketing',
        title: 'Referral Source Tracking',
        value: 'Needs source field',
        status: 'missing',
        description: 'Where new matters originate so budget can favor the highest-yielding channels.',
        note: 'Add referral source to case intake or customer agent lead capture.',
      },
    ],
    experience: [
      {
        group: 'Client Experience',
        title: 'NPS / Client Satisfaction',
        value: 'Needs surveys',
        status: 'missing',
        description: 'Post-matter satisfaction score showing likelihood of referrals and repeat business.',
        note: 'Add case-closing surveys to calculate NPS and satisfaction trends.',
      },
      {
        group: 'Client Experience',
        title: 'Average Case Duration',
        value: metrics.averageCaseDays === null ? 'No closed cases' : `${Math.round(metrics.averageCaseDays)} days`,
        status: metrics.averageCaseDays === null ? 'missing' : 'tracked',
        description: 'Average time from case intake to closure for completed matters.',
        note: 'Calculated from closed cases using created and updated dates.',
      },
    ],
  };
}

function sum(items, getValue) {
  return items.reduce((total, item) => total + Number(getValue(item) || 0), 0);
}

function percent(numerator, denominator) {
  return denominator > 0 ? (numerator / denominator) * 100 : null;
}

function average(values) {
  return values.length ? values.reduce((total, value) => total + value, 0) / values.length : null;
}

function daysBetween(start, end) {
  if (!start || !end) return null;
  const startTime = new Date(start).getTime();
  const endTime = new Date(end).getTime();
  if (Number.isNaN(startTime) || Number.isNaN(endTime)) return null;
  return Math.max(0, (endTime - startTime) / (1000 * 60 * 60 * 24));
}

function unique(values) {
  return Array.from(new Set(values.filter(Boolean)));
}

function rangeStatus(value, min, max) {
  if (value === null) return 'missing';
  if (value >= min && value <= max) return 'good';
  return 'watch';
}

function formatPercent(value) {
  return value === null ? 'No data' : `${value.toFixed(1)}%`;
}

function formatNumber(value) {
  return Number(value || 0).toFixed(2);
}

function money(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}
